import { useEffect, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import { ICONS } from "../constants/icons";
import { useUser } from "../contexts/UserContext";
import { get, ref, remove, update } from "firebase/database";
import { database } from "../constants/firebase";
import { ROUTES } from "../App";
import useMoveTo from "../constants/navigation";
import MobileHeader from "../components/MobileHeader";
import { getMonthKey } from "../utils/utils";



const CreateChallenge = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [challenge, setChallenge] = useState(null);
    const [form, setForm] = useState({
        title: "",
        description: "",
        prizeType: "cash", // cash | zylen
        prizeValue: "",
        deadline: "",
    });

    const fetchChallenge = async () => {
        const monthKey = getMonthKey();
        const snap = await get(ref(database, `Challenges/${monthKey}`));

        if (snap.exists()) {
            setChallenge(snap.val());
        } else {
            setChallenge(null);
        }
    };

    useEffect(() => {
        fetchChallenge();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };


    const handleSave = async () => {
        if (!currentUser) return;
        if (!currentUser?.isAdmin) {
            window.showToast("Only admin can create challenges", "error");
            return;
        }

        setIsSubmitting(true);

        try {
            const monthKey = getMonthKey();
            const challengeRef = ref(database, `Challenges/${monthKey}`);

            const snapshot = await get(challengeRef);

            if (snapshot.exists()) {
                window.showToast("Challenge already created for this month", "error");
                return;
            }

            const challengeData = {
                title: form.title,
                description: form.description,
                prize: {
                    type: form.prizeType,
                    value: form.prizeValue,
                },
                deadline: new Date(form.deadline).getTime(),
                createdAt: Date.now(),
                createdBy: currentUser.uid,
            };

            await update(challengeRef, {
                ...challengeData,
                status: "active",
                monthKey
            });

            window.showToast("Challenge submitted successfully!", "success");
            navigate(ROUTES.DASHBOARD, { replace: true });

        } catch (err) {
            console.error("Error submitting challenge:", err);
            window.showToast("Failed to submit challenge", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = async () => {
        const monthKey = getMonthKey();

        await update(ref(database, `Challenges/${monthKey}`), {
            status: "closed"
        });
        fetchChallenge(); // Refresh local state after closing

        window.showToast("Challenge closed", "success");
    };


    const handleDelete = async () => {
        const monthKey = getMonthKey();

        await remove(ref(database, `Challenges/${monthKey}`));
        fetchChallenge(); // Refresh local state after deletion
        window.showToast("Challenge deleted", "success");
    };


    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            <MobileHeader title={'Create Challenge'} isBackBtn={true} />

            <div className="flex">
                <Sidebar />

                <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">
                            Create Challenge
                        </h2>
                        <div />
                    </div>

                    <div className="max-w-md mx-auto md:pt-10 pt-3 space-y-6">

                        {/* Text Fields */}
                        <input
                            type="text"
                            name="title"
                            placeholder="Title"
                            value={form.title}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
                bg-[#F5F9FE] dark:bg-white 
                text-black dark:text-black
                border border-gray-200 dark:border-0"
                        />

                        {/* Body / Description */}
                        <textarea
                            name="description"
                            placeholder="Challenge Description"
                            value={form.description}
                            onChange={handleChange}
                            rows={4}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
                bg-[#F5F9FE] dark:bg-white 
                text-black dark:text-black
                border border-gray-200 dark:border-0"
                        />

                        {/* Prize Type Selector */}
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setForm(prev => ({ ...prev, prizeType: "cash" }))}
                                className={`flex-1 py-3 rounded-xl border border-primary font-semibold ${form.prizeType === "cash" ? "bg-primary text-white" : ""
                                    }`}
                            >
                                Cash Prize
                            </button>

                            <button
                                type="button"
                                onClick={() => setForm(prev => ({ ...prev, prizeType: "zylen" }))}
                                className={`flex-1 py-3 rounded-xl border border-primary font-semibold ${form.prizeType === "zylen" ? "bg-primary text-white" : ""
                                    }`}
                            >
                                Zylen Prize
                            </button>
                        </div>

                        {/* Price Field */}
                        <input
                            type="text"
                            name="prizeValue"
                            placeholder="Enter Prize Amount"
                            value={form.prizeValue}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
                bg-[#F5F9FE] dark:bg-white 
                text-black dark:text-black
                border border-gray-200 dark:border-0"
                        />

                        {/* Deadline Field (Date + Time) */}
                        <input
                            type="datetime-local"
                            name="deadline"
                            value={form.deadline}
                            onChange={handleChange}
                            id="dateInput"
                            onClick={() => document.getElementById("dateInput").showPicker()} // Force date picker on click
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
                bg-[#F5F9FE] dark:bg-white 
                text-black dark:text-black
                border border-gray-200 dark:border-0"
                        />
                        <button
                            onClick={handleSave}
                            className="bg-primary text-white text-lg font-semibold py-3 rounded-xl w-full mt-4"
                        >
                            {isSubmitting ? "Creating..." : "Create Challenge"}
                        </button>
                        {currentUser?.isAdmin && challenge && (
                            <div className="flex gap-3 mt-4">

                                {/* 🔴 DELETE - show if challenge exists (any state) */}
                                <button
                                    onClick={handleDelete}
                                    className="flex-1 bg-red-500 text-white py-3 text-sm md:text-md rounded-xl font-semibold"
                                >
                                    Delete Challenge
                                </button>

                                {/* 🟡 CLOSE - show ONLY if active */}
                                {challenge.status === "active" && (
                                    <button
                                        onClick={handleClose}
                                        className="flex-1 bg-yellow-500 text-white py-3 text-sm md:text-md rounded-xl font-semibold"
                                    >
                                        Close Challenge
                                    </button>
                                )}

                            </div>
                        )}
                    </div>


                </main>
            </div>
        </div>
    );
};

export default CreateChallenge;
